import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HiOutlineMail, HiOutlineLockClosed, HiOutlineUser, HiOutlinePhone, HiOutlineEye, HiOutlineEyeOff, HiOutlineCheck, HiOutlineX, HiOutlineInformationCircle } from 'react-icons/hi';
import { useAuth, authErrorMessage } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Helper: phone validation (Bangladesh format, 11 digits, starts with 01).
 */
const isPhoneValid = (phone) => /^01[3-9]\d{8}$/.test((phone || '').replace(/[\s\-]/g, '').replace(/^\+?88/, ''));

/**
 * Helper: password strength score (0..4) — based on length + variety.
 * 0 = empty, 1 = weak, 2 = fair, 3 = good, 4 = strong
 */
const passwordStrength = (pw = '') => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 10) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
};

const STRENGTH_LABEL = ['নতুন পাসওয়ার্ড দিন', 'দুর্বল', 'মোটামুটি', 'ভালো', 'শক্তিশালী'];
const STRENGTH_COLOR = ['bg-ink-200', 'bg-rose-500', 'bg-accent-500', 'bg-sky-500', 'bg-brand-500'];

const Register = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // Derived state — pure functions, recomputed on each render
  const phoneOk = useMemo(() => isPhoneValid(formData.phone), [formData.phone]);
  const emailOk = useMemo(() => !formData.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email), [formData.email]);
  const passScore = useMemo(() => passwordStrength(formData.password), [formData.password]);
  const passwordsMatch = useMemo(
    () => formData.confirmPassword && formData.password === formData.confirmPassword,
    [formData.password, formData.confirmPassword]
  );
  const passwordsMismatch = useMemo(
    () => formData.confirmPassword && formData.password !== formData.confirmPassword,
    [formData.password, formData.confirmPassword]
  );

  // Either email or phone is sufficient; user gets visual feedback as they type
  const canSubmit = formData.name && (formData.email || formData.phone) && formData.password.length >= 6 && passwordsMatch && phoneOk;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name) { setError('নাম দিন।'); return; }
    if (!formData.email && !formData.phone) { setError('ইমেইল অথবা ফোন নম্বর কমপক্ষে একটি দিন।'); return; }
    if (formData.phone && !phoneOk) { setError('ফোন নম্বর ১১ ডিজিটের হতে হবে (01XXXXXXXXX)'); return; }
    if (formData.password.length < 6) { setError('পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে।'); return; }
    if (formData.password !== formData.confirmPassword) { setError('পাসওয়ার্ড দুটি মিলছে না।'); return; }

    setLoading(true);
    try {
      const data = await register(formData.name, formData.email, formData.phone, formData.password);
      navigate(data.user.role === 'admin' ? '/admin/dashboard' : '/worker/dashboard');
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-ink-50 dark:bg-ink-900">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-brand relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
        <div className="relative text-white max-w-md">
          <div className="text-7xl mb-6">🔧</div>
          <h1 className="text-4xl font-bold mb-4 leading-tight">প্লাম্বার সিস্টেম</h1>
          <p className="text-lg text-white/90 mb-8">নতুন অ্যাকাউন্ট তৈরি করে আপনার ব্যবসা পরিচালনা শুরু করুন।</p>

          {/* First-user hint */}
          <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 text-sm">
            <div className="flex items-start gap-2">
              <HiOutlineInformationCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold mb-1">প্রথম ব্যবহারকারী হিসেবে</p>
                <p className="text-white/80">প্রথম যিনি রেজিস্ট্রেশন করবেন তিনিই <strong>অ্যাডমিন</strong> হবেন। পরবর্তীতে অ্যাডমিন নতুন ওয়ার্কার যোগ করতে পারবেন।</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile brand */}
          <div className="lg:hidden text-center mb-6">
            <div className="inline-flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-brand flex items-center justify-center text-white text-2xl shadow-pop">🔧</div>
              <h1 className="text-2xl font-bold text-ink-900 dark:text-white">প্লাম্বার সিস্টেম</h1>
            </div>
          </div>

          {/* Mobile first-user banner */}
          <div className="lg:hidden mb-4 p-3 bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800 rounded-xl text-xs text-brand-800 dark:text-brand-200 flex items-start gap-2">
            <HiOutlineInformationCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span><strong>প্রথম ব্যবহারকারী</strong> অ্যাডমিন হবেন।</span>
          </div>

          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 dark:text-white">{t('register')}</h2>
            <p className="text-sm text-ink-500 dark:text-ink-400 mt-2">{t('createAccount')}</p>
          </div>

          <div className="card shadow-card">
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {error && (
                <div
                  role="alert"
                  className="p-3 bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 rounded-xl text-sm flex items-start gap-2 border border-rose-200 dark:border-rose-800 animate-slide-up"
                >
                  <span className="text-base flex-shrink-0 mt-0.5">⚠️</span>
                  <span className="flex-1 leading-snug">{error}</span>
                  <button
                    type="button"
                    onClick={() => setError('')}
                    aria-label="Dismiss error"
                    className="text-rose-400 hover:text-rose-700 dark:hover:text-rose-200 px-1"
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Name */}
              <div>
                <label className="input-label">{t('name')} <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <HiOutlineUser className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-field pl-11"
                    placeholder="আপনার পুরো নাম"
                    required
                  />
                </div>
              </div>

              {/* Email (optional) */}
              <div>
                <label className="input-label">
                  {t('email')} <span className="text-ink-400 font-normal text-xs">(ঐচ্ছিক)</span>
                </label>
                <div className="relative">
                  <HiOutlineMail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field pl-11 pr-11"
                    placeholder="name@example.com"
                    autoComplete="email"
                  />
                  {formData.email && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {emailOk ? (
                        <HiOutlineCheck className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                      ) : (
                        <HiOutlineX className="w-5 h-5 text-rose-500" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Phone (preferred) */}
              <div>
                <label className="input-label">
                  {t('phone')} <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <HiOutlinePhone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400" />
                  <input
                    type="tel"
                    inputMode="numeric"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field pl-11 pr-11 font-mono tracking-wide"
                    placeholder="01XXXXXXXXX"
                    autoComplete="tel"
                    maxLength={14}
                    required
                  />
                  {formData.phone && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {phoneOk ? (
                        <HiOutlineCheck className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                      ) : (
                        <HiOutlineX className="w-5 h-5 text-rose-500" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="input-label">{t('password')} <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <HiOutlineLockClosed className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400" />
                  <input
                    type={showPass ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-field pl-11 pr-11"
                    placeholder="কমপক্ষে ৬ অক্ষর"
                    autoComplete="new-password"
                    required
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-ink-400 hover:text-ink-700 dark:hover:text-ink-200" tabIndex={-1}>
                    {showPass ? <HiOutlineEyeOff className="w-5 h-5" /> : <HiOutlineEye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Strength meter */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full transition-colors ${i <= passScore ? STRENGTH_COLOR[passScore] : 'bg-ink-200 dark:bg-ink-700'}`}
                        />
                      ))}
                    </div>
                    <p className="text-xs flex items-center justify-between">
                      <span className={passScore >= 3 ? 'text-brand-700 dark:text-brand-300 font-semibold' : passScore >= 2 ? 'text-sky-700 dark:text-sky-300' : 'text-rose-600 dark:text-rose-400'}>
                        {STRENGTH_LABEL[passScore]}
                      </span>
                      <span className="text-ink-400">{formData.password.length}/∞</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label className="input-label">{t('confirmPassword')} <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <HiOutlineLockClosed className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400" />
                  <input
                    type={showPass ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="input-field pl-11 pr-11"
                    placeholder="পাসওয়ার্ড আবার লিখুন"
                    autoComplete="new-password"
                    required
                  />
                  {formData.confirmPassword && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {passwordsMatch ? (
                        <HiOutlineCheck className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                      ) : (
                        <HiOutlineX className="w-5 h-5 text-rose-500" />
                      )}
                    </div>
                  )}
                </div>
                <p className={`text-xs mt-1.5 flex items-center gap-1 ${passwordsMismatch ? 'text-rose-600 dark:text-rose-400' : passwordsMatch ? 'text-brand-700 dark:text-brand-300 font-semibold' : 'text-ink-500 dark:text-ink-400'}`}>
                  {passwordsMismatch ? '⚠ পাসওয়ার্ড মিলছে না'
                    : passwordsMatch ? '✓ পাসওয়ার্ড মিলেছে'
                      : <>উপরের পাসওয়ার্ডটি আবার লিখুন</>}
                </p>
              </div>

              <button type="submit" disabled={loading || !canSubmit} className="btn-primary btn-lg w-full">
                {loading ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('loading')}
                  </>
                ) : (
                  t('register')
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-ink-600 dark:text-ink-400">
              {t('alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-semibold">
                {t('login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
